import difference from 'lodash-es/difference';
import { createSelector } from 'reselect';
import { getOrdersData } from '~/selectors/orders.selectors';
import { getSelectedRates } from '~/selectors/rates.selectors';
import APIMessage from '~/types/APIMessage';
import Order, { OrderSelection, OrderValidationStatus } from '~/types/Order';
import OrdersBatch, {
  BatchEntry,
  BatchEntryStatus,
  BatchGroupStatus,
  BatchOrder,
  BatchShipment,
  BatchSteps,
  BatchTransaction,
  isFailedBatchEntry,
  UserBatchInput,
} from '~/types/OrdersBatch';
import { isShipmentCompleted } from '~/types/Shipment';
import { TransactionStatus } from '~/types/Transaction';
import { ApplicationState } from '../store';
import { getTransactionsData } from './transactions.selectors';

export const getOrderIdFromProps = (_: unknown, props: { orderId: string }) => props.orderId;

export const getShipmentObjectIdFromProps = (_: unknown, props: { shipmentObjectId: string }) =>
  props.shipmentObjectId;

export const getBatchEntryIdFromProps = (_: unknown, props: { batchEntryId: number }) =>
  props.batchEntryId;

export const getBatchStore = (state: ApplicationState) => state.ordersBatch;

export const getBatchIsLoading = createSelector(
  getBatchStore,
  (batchStore): boolean => batchStore.isLoadingBatch,
);

export const getBatchData = createSelector(
  getBatchStore,
  (batchStore): Maybe<Partial<OrdersBatch>> => batchStore.data,
);

export const getBatchOrders = createSelector(getBatchStore, (batchStore) => batchStore.orders);

export const getBatchOrdersLength = createSelector(
  getBatchOrders,
  (orders): number => orders.allObjectIds.length,
);

export const getBatchEntries = createSelector(
  getBatchStore,
  (batchStore) => batchStore.batchEntries,
);

export const getBatchShipments = createSelector(
  getBatchStore,
  (batchStore) => batchStore.shipments,
);

export const getHasPollingTimedOut = createSelector(
  getBatchStore,
  (batchStore): boolean => batchStore.hasPollingTimedOut,
);

export const getBatchTransactions = createSelector(
  getBatchStore,
  (batchStore) => batchStore.transactions,
);

export const getBatchOrdersData = createSelector(getBatchOrders, (batchOrders) => batchOrders.data);

export const getBatchOrdersAllIds = createSelector(
  getBatchOrders,
  (batchOrders): readonly string[] => batchOrders.allObjectIds,
);

export const getOrder = createSelector(
  getOrdersData,
  getOrderIdFromProps,
  (ordersData, orderObjectId: string): Order => ordersData[orderObjectId],
);

export const getBatchEntriesData = createSelector(
  getBatchEntries,
  (batchEntries) => batchEntries.data,
);

export const getBatchEntriesByOrder = createSelector(
  getBatchEntries,
  (batchEntries) => batchEntries.byOrderObjectId,
);

export const getBatchShipmentsData = createSelector(
  getBatchShipments,
  (batchShipments) => batchShipments.data,
);

export const getBatchShipmentByShipmentId = createSelector(
  getBatchShipmentsData,
  getShipmentObjectIdFromProps,
  (batchShipmentData, shipmentObjectId: string): BatchShipment =>
    batchShipmentData[shipmentObjectId],
);

export const getBatchEntryByShipmentId = createSelector(
  getBatchShipmentByShipmentId,
  getBatchEntriesData,
  (batchShipment, batchEntries) => batchEntries[batchShipment.batchEntryId],
);

export const getBatchEntryFromOrderId = createSelector(
  getBatchShipmentByShipmentId,
  getBatchEntriesData,
  (batchShipment, batchEntries) => batchEntries[batchShipment.batchEntryId],
);

export const getBatchShipmentsByBatchEntry = createSelector(
  getBatchShipments,
  (batchShipments) => batchShipments.byBatchEntryId,
);

export const getBatchTransactionsData = createSelector(
  getBatchTransactions,
  (batchTransactions) => batchTransactions.data,
);

export const getBatchTransactionsByBatchEntry = createSelector(
  getBatchTransactions,
  (batchTransactions) => batchTransactions.byBatchEntryId,
);

export const getBatchExcludedOrderIds = createSelector(
  getBatchStore,
  (batchStore) => batchStore.excludedOrdersIds,
);

export const getBatchOrder = createSelector(
  getBatchOrdersData,
  getOrderIdFromProps,
  (batchOrdersData, orderObjectId: string): BatchOrder => batchOrdersData[orderObjectId],
);

export const getBatchId = createSelector(getBatchData, (batchData) =>
  batchData ? batchData.id : null,
);

export const getOutboundBatchEntryFromOrderId = createSelector(
  getBatchEntriesData,
  getBatchEntriesByOrder,
  getOrderIdFromProps,
  (batchEntriesData, batchEntriesByOrder, orderId: string): Maybe<BatchEntry> =>
    Object.values(batchEntriesData).find(
      (batchEntry) => batchEntriesByOrder[orderId].includes(batchEntry.id) && !batchEntry.isReturn,
    ) || null,
);

export const getOutboundBatchEntryIdFromOrderId = createSelector(
  getOutboundBatchEntryFromOrderId,
  (batchEntry: Maybe<BatchEntry>) => (batchEntry ? batchEntry.id : null),
);

export const getBatchOrderBatchEntries = createSelector(
  getBatchOrder,
  getBatchEntriesData,
  getBatchEntriesByOrder,
  (batchOrder, batchEntriesData, batchEntriesByOrder): BatchEntry[] =>
    batchOrder
      ? (batchEntriesByOrder[batchOrder.objectId] || []).map(
          (batchEntryId) => batchEntriesData[batchEntryId],
        )
      : [],
);

export const getOutboundBatchEntry = createSelector(
  getBatchOrderBatchEntries,
  (batchEntries: BatchEntry[]): Maybe<BatchEntry> =>
    batchEntries.find((batchEntry) => !batchEntry.isReturn) || null,
);

export const getReturnBatchEntry = createSelector(
  getBatchOrderBatchEntries,
  (batchEntries: BatchEntry[]): Maybe<BatchEntry> =>
    batchEntries.find((batchEntry) => batchEntry.isReturn) || null,
);

export const getBatchEntryOutboundShipment = createSelector(
  getOutboundBatchEntry,
  getBatchShipmentsData,
  getBatchShipmentsByBatchEntry,
  (outboundBatchEntry, batchShipmentsData, batchShipmentsByBatchEntry): Maybe<BatchShipment> =>
    outboundBatchEntry
      ? (batchShipmentsByBatchEntry[outboundBatchEntry.id] || []).map(
          (shipmentObjectId) => batchShipmentsData[shipmentObjectId],
        )[0]
      : null,
);

export const getBatchEntryOutboundTransaction = createSelector(
  getOutboundBatchEntry,
  getBatchTransactionsData,
  getBatchTransactionsByBatchEntry,
  (outboundBatchEntry, batchTransactionsData, batchTransactionsByEntry): Maybe<BatchTransaction> =>
    outboundBatchEntry
      ? (batchTransactionsByEntry[outboundBatchEntry.id] || []).map(
          (transactionObjectId) => batchTransactionsData[transactionObjectId],
        )[0]
      : null,
);

export const getBatchEntryReturnTransaction = createSelector(
  getReturnBatchEntry,
  getBatchTransactionsData,
  getBatchTransactionsByBatchEntry,
  (returnBatchEntry, batchTransactionsData, batchTransactionsByEntry): Maybe<BatchTransaction> =>
    returnBatchEntry
      ? (batchTransactionsByEntry[returnBatchEntry.id] || []).map(
          (transactionObjectId) => batchTransactionsData[transactionObjectId],
        )[0]
      : null,
);

export const getFirstShipmentSelectedRateObjectId = createSelector(
  getSelectedRates,
  getBatchEntryOutboundShipment,
  (selectedRates, outboundShipment): Maybe<string> =>
    outboundShipment ? selectedRates[outboundShipment.objectId] : null,
);

export const getOutboundShipmentSelectedRateObjectIdFromBatch = createSelector(
  getBatchEntryOutboundShipment,
  (outboundShipment): Maybe<string> =>
    outboundShipment ? outboundShipment.selectedRateObjectId : null,
);

export const getNotExcludedOrdersIds = createSelector(
  getBatchOrdersAllIds,
  getBatchExcludedOrderIds,
  (allIds, excludedIds): string[] => difference(allIds, excludedIds),
);

export const getNotExcludedOrders = createSelector(
  getOrdersData,
  getNotExcludedOrdersIds,
  (ordersData, notExcludedOrderIds): Order[] =>
    notExcludedOrderIds.map((orderId) => ordersData[orderId]).filter((v) => v),
);

export const getNotExcludedBatchEntries = createSelector(
  getBatchEntriesData,
  getBatchExcludedOrderIds,
  (batchEntriesData, excludedOrdersIds) =>
    Object.values(batchEntriesData).filter(
      (batchEntry) => !excludedOrdersIds.includes(batchEntry.orderObjectId),
    ),
);

export const getBatchInvalidOrders = createSelector(
  getNotExcludedOrders,
  getBatchEntries,
  (notExcludedOrders, batchEntries): Order[] => {
    return notExcludedOrders.filter((order) => {
      const entriesByOrder = batchEntries.byOrderObjectId[order.objectId];

      if (!entriesByOrder) return false;

      // We just get the first batch entry id
      const batchEntryId = entriesByOrder[0];
      const batchEntry = batchEntries.data[batchEntryId];

      return batchEntry.objectStatus === BatchEntryStatus.INVALID_ORDER;
    });
  },
);

export const getBatchValidOrders = createSelector(
  getNotExcludedOrders,
  getBatchEntries,
  (notExcludedOrders, batchEntries): Order[] => {
    return notExcludedOrders.filter((order) => {
      const entriesByOrder = batchEntries.byOrderObjectId[order.objectId];

      if (!entriesByOrder) return false;

      // We just get the first batch entry id
      const batchEntryId = entriesByOrder[0];
      const batchEntry = batchEntries.data[batchEntryId];

      return batchEntry.objectStatus !== BatchEntryStatus.INVALID_ORDER;
    });
  },
);

export const getIncompleteBatchOrders = createSelector(
  getNotExcludedOrders,
  (notExcludedOrders): Order[] =>
    notExcludedOrders.filter((order) => order.validationStatus === OrderValidationStatus.INVALID),
);

export const getBatchCurrentStep = createSelector(
  getBatchStore,
  (batchStore): BatchSteps => batchStore.currentStep,
);

export const getWasOrdersFetchTriggered = createSelector(
  getBatchStore,
  (batchStore): boolean => batchStore.wasOrdersFetchTriggered,
);

export const getOrdersWithTransactions = createSelector(
  getBatchOrdersData,
  getBatchEntriesByOrder,
  getBatchTransactionsByBatchEntry,
  (batchOrders, batchEntriesByOrder, batchTransactionsByEntry): BatchOrder[] =>
    Object.values(batchOrders).filter((batchOrder: BatchOrder) =>
      (batchEntriesByOrder[batchOrder.objectId] || []).some(
        (batchEntryId: number) => (batchTransactionsByEntry[batchEntryId] || []).length !== 0,
      ),
    ),
);

export const getSuccessfulBatchTransactions = createSelector(
  getBatchTransactions,
  (batchTransactions): BatchTransaction[] =>
    Object.values(batchTransactions.data).filter(
      (batchTransaction) => batchTransaction.objectStatus === TransactionStatus.SUCCESS,
    ),
);

export const getEntriesWithSuccessfulTransactions = createSelector(
  getBatchEntries,
  getSuccessfulBatchTransactions,
  (batchEntries, batchTransactions) =>
    batchTransactions
      .map((batchTransaction) => ({
        orderObjectId: batchEntries.data[batchTransaction.batchEntryId].orderObjectId,
        batchEntryId: batchTransaction.batchEntryId,
      }))
      .reduce<{
        batchEntryIds: number[];
        orderObjectIds: string[];
        orderSelection: OrderSelection;
      }>(
        (entries, { orderObjectId, batchEntryId }) => {
          entries.orderObjectIds.push(orderObjectId);
          entries.batchEntryIds.push(batchEntryId);
          if (entries.orderSelection[orderObjectId] === undefined) {
            entries.orderSelection = { ...entries.orderSelection, [orderObjectId]: [] };
          }

          entries.orderSelection[orderObjectId].push(batchEntryId);

          return entries;
        },
        {
          batchEntryIds: [],
          orderObjectIds: [],
          orderSelection: {},
        },
      ),
);

export const getSuccessfulOutboundFailedReturnBatchEntries = createSelector(
  getBatchEntriesData,
  getBatchEntriesByOrder,
  getEntriesWithSuccessfulTransactions,
  (batchEntriesData, batchEntriesByOrder, entriesWithSuccessfulTxns): BatchEntry[] =>
    batchEntriesData
      ? entriesWithSuccessfulTxns.orderObjectIds.reduce((list: BatchEntry[], successfulOrderId) => {
          let outboundBatchEntry;
          let returnBatchEntry;
          batchEntriesByOrder[successfulOrderId].forEach((batchEntryId) => {
            const batchEntry = batchEntriesData[batchEntryId];
            if (batchEntry.isReturn) {
              returnBatchEntry = batchEntry;
            } else {
              outboundBatchEntry = batchEntry;
            }
          });

          if (!(outboundBatchEntry && returnBatchEntry)) {
            return list;
          }

          if (isFailedBatchEntry(returnBatchEntry)) {
            return [outboundBatchEntry, ...list];
          }

          return list;
        }, [])
      : [],
);

export const getOrderIdsWithTransactions = createSelector(
  getOrdersWithTransactions,
  (ordersWithTransactions: BatchOrder[]) =>
    ordersWithTransactions.map((batchOrder) => batchOrder.objectId),
);

export const getIsSavingPackageInformation = createSelector(
  getBatchStore,
  (batchStore): boolean => batchStore.isSavingPackageInformation,
);

export const getOrderBatchTransactionStatus = createSelector(
  getBatchData,
  (batchData: Maybe<Partial<OrdersBatch>>): Maybe<BatchGroupStatus> =>
    batchData && batchData.transactionGroupStatus ? batchData.transactionGroupStatus : null,
);

export const getOrderBatchReturnTransactionStatus = createSelector(
  getBatchData,
  (batchData: Maybe<Partial<OrdersBatch>>): Maybe<BatchGroupStatus> =>
    batchData && batchData.returnTransactionGroupStatus
      ? batchData.returnTransactionGroupStatus
      : null,
);

export const getSelectedServiceLevel = createSelector(
  getBatchStore,
  (batchStore) => batchStore.selectedServiceLevelForAllOrders,
);

export const getIgnoredShipmentIds = createSelector(
  getBatchStore,
  getSelectedRates,
  (batchStore, ratesData) =>
    Object.keys(batchStore.shipments.data).filter(
      (shipmentObjectId) => !ratesData[shipmentObjectId],
    ),
);

export const getIgnoredBatchEntries = createSelector(
  getIgnoredShipmentIds,
  getBatchStore,
  (ignoredShipmentIds, batchStore) =>
    ignoredShipmentIds.reduce((ignoredBatchEntries: BatchEntry[], ignoredShipmentId) => {
      const batchShipment = batchStore.shipments.data[ignoredShipmentId];
      const batchEntry = batchStore.batchEntries.data[batchShipment.batchEntryId];
      const excludedOrder = batchStore.excludedOrdersIds.find(
        (excludedOrderId) => excludedOrderId === batchEntry.orderObjectId,
      );

      if (batchEntry.isReturn || excludedOrder) {
        return ignoredBatchEntries;
      }

      return [...ignoredBatchEntries, batchEntry];
    }, []),
);

export const getIgnoredOrdersIds = createSelector(getIgnoredBatchEntries, (ignoredBatchEntries) =>
  ignoredBatchEntries.map((ignoredBatchEntry: BatchEntry) => ignoredBatchEntry.orderObjectId),
);

export const hasPurchasedLabels = createSelector(
  getBatchData,
  (batchData: Maybe<Partial<OrdersBatch>>): boolean =>
    batchData && batchData.transactionGroupStatus
      ? [BatchGroupStatus.PROCESSING, BatchGroupStatus.COMPLETE].includes(
          batchData.transactionGroupStatus,
        )
      : false,
);

export const isCreatingTransactions = createSelector(
  getBatchStore,
  (batchStore) => batchStore.isCreatingTransactions,
);

export const isInlineEditModalOpen = createSelector(
  getBatchStore,
  (batchStore) => batchStore.inlineEditModalOpen,
);

export const isInlineEditSlideContentOpen = createSelector(
  getBatchStore,
  (batchStore): boolean => batchStore.inlineEditModalShowSlideContent,
);

export const getEditingOrderId = createSelector(
  getBatchStore,
  (batchStore): string => batchStore.inlineEditOrderId,
);

export const getUserBatchInputFromBatchEntryId = createSelector(
  getBatchEntriesData,
  getBatchEntryIdFromProps,
  (batchEntriesData, batchEntryId): Maybe<UserBatchInput> =>
    batchEntriesData[batchEntryId].userInput || null,
);

export const getIsFetchingUserBatchInput = createSelector(
  getBatchStore,
  (batchStore) => batchStore.isFetchingUserBatchInput,
);

export const getShipmentsWithoutRates = createSelector(
  getBatchShipmentsData,
  (batchShipmentsData): BatchShipment[] =>
    Object.values(batchShipmentsData).filter(
      (batchShipment) =>
        batchShipment.numRates === 0 && isShipmentCompleted(batchShipment.objectStatus),
    ),
);

export const getFailedShipmentsBatchEntries = createSelector(
  getBatchEntriesData,
  (batchEntriesData): BatchEntry[] =>
    Object.values(batchEntriesData).filter(
      (batchEntry) => batchEntry.objectStatus === BatchEntryStatus.SHIPMENT_REQUEST_FAILED,
    ),
);

export const getFailedTransactionBatchEntries = createSelector(
  getBatchEntriesData,
  getBatchTransactions,
  getBatchTransactionsData,
  (batchEntriesData, transactions, transactionsData): BatchEntry[] =>
    Object.values(batchEntriesData).filter(
      (batchEntry) =>
        [BatchEntryStatus.TRANSACTION_REQUEST_FAILED, BatchEntryStatus.RATE_NOT_AVAILABLE].includes(
          batchEntry.objectStatus,
        ) ||
        (transactions.byBatchEntryId[batchEntry.id] &&
          transactionsData[transactions.byBatchEntryId[batchEntry.id][0]].objectStatus ===
            TransactionStatus.ERROR),
    ),
);

/**
 * Returns only the failed batch entries that have an order number
 */
export const getFailedBatchEntriesWithOrderNumbers = createSelector(
  getFailedTransactionBatchEntries,
  getOrdersData,
  (failedBatchEntries, ordersData): BatchEntry[] =>
    Object.values(failedBatchEntries).filter(
      (entry) => ordersData[entry.orderObjectId]?.orderNumber,
    ),
);

/**
 * Returns messages for failed transaction batch entries mapped by order ID.
 *
 * If batch entry is associated with transaction, only return messages for fetched transaction.
 */
export const getFailedBatchEntriesMessages = createSelector(
  getFailedTransactionBatchEntries,
  getBatchTransactions,
  getTransactionsData,
  (failedBatchEntries, batchTransactions, transactionsData): HashMap<string, APIMessage[]> =>
    failedBatchEntries.reduce((messagesByOrderId, batchEntry) => {
      const messages = [
        BatchEntryStatus.TRANSACTION_REQUEST_FAILED,
        BatchEntryStatus.RATE_NOT_AVAILABLE,
      ].includes(batchEntry.objectStatus)
        ? [
            /**
             * TODO: temporary message copied from LabelsCell.tsx. Actual message should
             * probably come from backend
             */
            {
              kind: 'APIMessage',
              code: batchEntry.objectStatus,
              source: 'batch-entry',
              text:
                batchEntry.objectStatus === BatchEntryStatus.TRANSACTION_REQUEST_FAILED
                  ? 'There was an error processing your request. Please try again later.'
                  : 'Could not auto create return.',
            } as APIMessage,
          ]
        : // Get messages if transaction data is available
          transactionsData[batchTransactions.byBatchEntryId[batchEntry.id][0]]
          ? transactionsData[batchTransactions.byBatchEntryId[batchEntry.id][0]].messages
          : null;

      return messages
        ? {
            ...messagesByOrderId,
            [batchEntry.orderObjectId]: messages,
          }
        : messagesByOrderId;
    }, {}),
);

export const getInvalidShipmentsNumber = createSelector(
  getShipmentsWithoutRates,
  getFailedShipmentsBatchEntries,
  (shipmentsWithoutRates, failedShipmentBatchEntries) =>
    shipmentsWithoutRates.length + failedShipmentBatchEntries.length,
);

export const getSelectedServiceLevelForAllOrders = createSelector(
  getBatchStore,
  (batchStore): Maybe<string> => batchStore.selectedServiceLevelForAllOrders,
);

export const getSelectedRatesForValidOrders = createSelector(
  getBatchValidOrders,
  getBatchEntriesByOrder,
  getBatchEntriesData,
  getBatchShipmentsByBatchEntry,
  getSelectedRates,
  (
    batchValidOrders,
    batchEntriesByOrder,
    batchEntriesData,
    shipmentsByEntry,
    selectedRates,
  ): string[] => {
    return batchValidOrders.reduce((list: string[], validOrder) => {
      const entries = batchEntriesByOrder[validOrder.objectId];
      const batchEntries = entries
        .map((entry) => batchEntriesData[entry])
        .filter((batchEntry) => !batchEntry.isReturn);

      if (batchEntries.length === 0) return list;

      const shipments = shipmentsByEntry[batchEntries[0].id];

      if (!shipments || shipments.length !== 1) return list;

      const selectedRate = selectedRates[shipments[0]];

      if (!selectedRate) return list;

      return [...list, selectedRate];
    }, []);
  },
);

export const getBatchCreationError = createSelector(
  getBatchStore,
  (batchStore): Maybe<string> => batchStore.error,
);
